/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap as unboundedCssMap } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import {
	ModifiedOnElement,
	OwnedByGroupElement,
	SmartLinkSize,
	SmartLinkTheme,
	UnresolvedAction,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import { CardContainer, useTrackedSmartCardState } from '@atlassian/company-hub-common';
import { getImageTypeToShow, ImageRenderer } from '@atlassian/company-hub-common/media-components';

import { useSmartCardElementsRenderState } from '../../../../../../controllers/use-smart-card-elements-render-state';
import { CardTitle, FontVariant } from '../../../text-elements';
import type { CommonCardProps } from '../../../types';

import { messages } from './messages';

const unboundedStyles = unboundedCssMap({
	content: {
		display: 'flex',
		flexDirection: 'row',
		overflow: 'hidden',
		alignItems: 'start',
	},
	avatar: {
		// prevent avatar dimension from fluctuating and affecting vertical space distribution
		maxHeight: '24px',
		marginTop: '-4px',

		// Avatar is left-indented by left padding on ul , which is introduced by Prosemirror.
		// It was fixed for other type of smart card in packages/editor/editor-common/src/styles/shared/smart-card.ts
		// However, the FlexibleCard remains unfixed because it doesn't have a specific class name.
		// Hence, we need to fix it here until a more permanent solution is available.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"span[data-smart-element='OwnedByGroup'] > ul": {
			padding: 0,
			margin: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > li': {
				margin: 0,
			},
		},
	},
});

const styles = cssMap({
	body: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		overflow: 'hidden',
		height: '100%',
	},
	bodyWithImage: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		flex: '0 0 auto',
		maxWidth: '72%',
		display: 'flex',
	},
	bodyWithoutImage: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	image: {
		overflow: 'hidden',
		height: '100%',
		flex: '1 1 28%',
		maxWidth: '28%',
		display: 'flex',
	},
	unresolvedAction: {
		width: '100%',
		overflow: 'hidden',
		font: token('font.body'),
		textOverflow: 'ellipsis',
		color: token('color.text.subtle'),
	},
});

export const SmallCardV1 = ({
	cardDimensions,
	cardId,
	link,
	title,
	imageSrc,
	isAvatarShown,
	isPublishDateShown,
	isInViewMode,
	imageAltText,
	contentId,
	isManualVariant,
	extensionLocalId,
	onCardSucceeded,
	onCardFailed,
	backgroundColor,
	textColor,
	cornerStyle,
}: CommonCardProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const cardHeight = cardDimensions.height;

	const hasLink = !!link;

	const {
		errorCardStatus: hasError,
		trackAndCategorizeError,
		trackSuccess,
		isResolved,
	} = useTrackedSmartCardState(cardId, link, onCardSucceeded, onCardFailed);

	const {
		setIsAvatarAvailable,
		setIsModifiedOnAvailable,
		shouldRenderMetadata,
		shouldRenderOwnerInfo,
	} = useSmartCardElementsRenderState({
		isOwnerInfoShown: isAvatarShown && hasLink,
		isModifiedOnShown: isPublishDateShown && hasLink,
	});

	const { hasImage } = getImageTypeToShow({ imageSrc });

	const cardBodyItem = hasLink
		? [
				title && (
					<CardTitle
						key="title"
						maxLine={shouldRenderMetadata ? 2 : 3}
						fontVariant={FontVariant.HeadingXSmall}
					>
						{title}
					</CardTitle>
				),
				hasError && !title && (
					<Box key="unresolved-action" xcss={cx(styles.unresolvedAction)}>
						<UnresolvedAction />
					</Box>
				),
				isResolved && !hasError && shouldRenderMetadata && (
					<Inline
						testId="small-card-v1-metadata"
						key="metadata"
						space="space.050"
						alignBlock="center"
					>
						{shouldRenderOwnerInfo && (
							<div css={unboundedStyles.avatar}>
								<OwnedByGroupElement onRender={setIsAvatarAvailable} />
							</div>
						)}
						{!!isPublishDateShown && (
							<ModifiedOnElement color="inherit" onRender={setIsModifiedOnAvailable} />
						)}
					</Inline>
				),
			].filter(Boolean)
		: [
				<CardTitle key="title" maxLine={3} fontVariant={FontVariant.HeadingXSmall}>
					{title || formatMessage(messages.emptyStatePlaceholderTitle)}
				</CardTitle>,
			].filter(Boolean);

	const cardContent = (
		<div
			data-testid="small-card-content"
			css={unboundedStyles.content}
			style={{
				height: cardHeight,
			}}
		>
			{hasImage && (
				<Box xcss={cx(styles.image)}>
					<ImageRenderer
						contentId={contentId}
						imageSrc={imageSrc}
						imageAlt={imageAltText}
						shouldShowExternalSiteImage={hasLink}
					/>
				</Box>
			)}

			<Stack
				testId="small-card-body"
				space="space.100"
				xcss={cx(
					styles.body,
					hasImage && styles.bodyWithImage,
					!hasImage && styles.bodyWithoutImage,
				)}
				alignBlock="center"
				alignInline="start"
			>
				{cardBodyItem}
			</Stack>
		</div>
	);

	return (
		<CardContainer
			id="small-card-v1-container"
			cardHeight={cardHeight}
			isInViewMode={isInViewMode}
			cardId={cardId}
			extensionLocalId={extensionLocalId}
			isEditable={isManualVariant}
			cornerStyle={cornerStyle}
			hasLink={hasLink}
			backgroundColor={backgroundColor}
			textColor={textColor}
		>
			{hasLink ? (
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: isInViewMode,
						size: SmartLinkSize.Large,
						removeBlockRestriction: true,
						hideBackground: true,
						hideElevation: true,
						hidePadding: true,
					}}
					url={link}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{cardContent}
				</SmartCardSSR>
			) : (
				cardContent
			)}
		</CardContainer>
	);
};
